import { ExamCenterProctorRequestType } from '@exam-center/graphql';
import { ALLOW_FILE_TYPES } from '@exam-center/proctor-request-management';
import { ID_CARD_NUMBER_MASK, PHONE_NUMBER_MASK } from '@exam-center/utils';
import { englishTitleOptionSchema, thaiTitleOptionSchema } from '@shared/utils';
import dayjs from 'dayjs';
import IMask from 'imask';
import { z } from 'zod';

const MIN_REQUIRED_FIELD_LENGTH = 1;
const MAX_NAME_LENGTH = 100;
const MAX_ADDRESS_LENGTH = 1024;
const MAX_EMAIL_LENGTH = 256;
const MAX_FILE_LENGTH = 4;
const MAX_UPLOAD_SIZE = 1024 * 1024 * 20; // 20MB

const signedUrlSchema = z.object({
  url: z.string().url(),
  name: z.string(),
  key: z.string(),
  type: z.string(),
  size: z.number(),
});

export type SignedUrlInput = z.infer<typeof signedUrlSchema>;

const fileSchema = z.instanceof(File).refine((file) => {
  return ALLOW_FILE_TYPES.includes(file.type);
}, 'รูปแบบไฟล์ไม่ถูกต้อง กรุณาอัปโหลดไฟล์ .jpg หรือ .jpeg หรือ .pdf เท่านั้น');

const attachedFileSchema = z
  .array(z.union([signedUrlSchema, fileSchema]))
  .min(MIN_REQUIRED_FIELD_LENGTH, { message: 'กรุณาแนบไฟล์อัปโหลด' })
  .max(MAX_FILE_LENGTH, { message: `ไม่สามารถอัปโหลดได้เกิน ${MAX_FILE_LENGTH} ไฟล์` })
  .refine(
    (files) => {
      return files.length && files.reduce((acc, file) => acc + file.size, 0) <= MAX_UPLOAD_SIZE;
    },
    {
      message: `ขนาดไฟล์รวมกันต้องไม่เกิน ${MAX_UPLOAD_SIZE} MB`,
    },
  );

export const DraftProctorRequestEditionFormSchema = z.object({
  proctorType: z.nativeEnum(ExamCenterProctorRequestType, {
    message: 'กรุณาเลือกประเภทผู้คุมสอบ',
  }),
  personal: z.object({
    idNumber: z
      .string()
      .trim()
      .min(MIN_REQUIRED_FIELD_LENGTH, {
        message: 'กรุณากรอกเลขบัตรประชาชน',
      })
      .refine(
        (val) => {
          const mask = IMask.createMask({ mask: ID_CARD_NUMBER_MASK });
          mask.resolve(val);
          return mask.isComplete;
        },
        { message: 'รูปแบบเลขบัตรประชาชนไม่ถูกต้อง ต้องมีจำนวน 13 หลักและเป็นตัวเลขเท่านั้น' },
      ),
    titleTh: thaiTitleOptionSchema,
    firstNameTh: z
      .string()
      .trim()
      .min(MIN_REQUIRED_FIELD_LENGTH, { message: 'กรุณากรอกชื่อภาษาไทย' })
      .max(MAX_NAME_LENGTH, `ความยาวต้องไม่เกิน ${MAX_NAME_LENGTH} ตัวอักษร`),
    lastNameTh: z
      .string()
      .trim()
      .min(MIN_REQUIRED_FIELD_LENGTH, { message: 'กรุณากรอกนามสกุลภาษาไทย' })
      .max(MAX_NAME_LENGTH, `ความยาวต้องไม่เกิน ${MAX_NAME_LENGTH} ตัวอักษร`),
    titleEn: englishTitleOptionSchema,
    firstNameEn: z
      .string()
      .trim()
      .min(MIN_REQUIRED_FIELD_LENGTH, { message: 'กรุณากรอกชื่อภาษาอังกฤษ' })
      .max(MAX_NAME_LENGTH, `ความยาวต้องไม่เกิน ${MAX_NAME_LENGTH} ตัวอักษร`),
    lastNameEn: z
      .string()
      .trim()
      .min(MIN_REQUIRED_FIELD_LENGTH, { message: 'กรุณากรอกนามสกุลภาษาอังกฤษ' })
      .max(MAX_NAME_LENGTH, `ความยาวต้องไม่เกิน ${MAX_NAME_LENGTH} ตัวอักษร`),
    email: z
      .string()
      .trim()
      .min(MIN_REQUIRED_FIELD_LENGTH, { message: 'กรุณากรอกอีเมลผู้ใช้' })
      .max(MAX_EMAIL_LENGTH, `ความยาวต้องไม่เกิน ${MAX_EMAIL_LENGTH} ตัวอักษร`)
      .email({
        message: 'รูปแบบอีเมลผู้ใช้ไม่ถูกต้อง',
      }),
    phoneNumber: z
      .string()
      .nullish()
      .refine(
        (val) => {
          if (!val) return true;
          const mask = IMask.createMask({ mask: PHONE_NUMBER_MASK });
          mask.resolve(val);
          return mask.isComplete;
        },
        { message: 'รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง' },
      ),
    address: z
      .string()
      .trim()
      .max(MAX_ADDRESS_LENGTH, `ความยาวต้องไม่เกิน ${MAX_ADDRESS_LENGTH} ตัวอักษร`),
  }),
  employeeInfo: z
    .object({
      code: z.string().trim().nullish(),
      position: z.string().trim().nullish(),
      department: z.string().trim().nullish(),
      termination: z
        .object({
          isAssigned: z.enum(['true', 'false']),
          endedAt: z.date().nullish(),
        })
        .refine(({ isAssigned, endedAt }) => isAssigned === 'false' || endedAt !== null, {
          message: 'ระบุวันที่สิ้นสุดปฏิบัติหน้าที่',
          path: ['endedAt'],
        }),
      startedAt: z.date({ message: 'กรุณาเลือกวันที่เริ่มปฏิบัติหน้าที่' }),
    })
    .refine(
      ({ termination, startedAt }) =>
        termination.isAssigned === 'false' ||
        dayjs(termination.endedAt).isSameOrAfter(dayjs(startedAt), 'date'),
      {
        message: 'วันที่สิ้นสุดปฏิบัติหน้าที่ต้องมากกว่าหรือเท่ากับวันที่เริ่มปฏิบัติหน้าที่',
        path: ['termination.endedAt'],
      },
    ),
  document: z.object({
    agreementForms: attachedFileSchema,
    identifications: attachedFileSchema,
  }),
});

export type DraftProctorRequestEditionFormInput = z.input<
  typeof DraftProctorRequestEditionFormSchema
>;
